import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Navbar from "../../Layouts/Navbar/Navbar";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import LeftSideBar, { FolderCollapse } from "../../components/SideBar/LeftSideBar";
import { useAxiosContext } from "../../context/AxiosContext";
import { useFormContext } from "../../context/FormContext";
import { useModalContext } from "../../context/ModalContext";
import { useTableContext } from "../../context/TableContext";
import { useTreeContext } from "../../context/TreeDataContext";
import CreateSubformModal from "./CreateSubformModal";
import "./Form.css";
import Loader from "../../Layouts/Loader/Loader";
import { TryAgainMessage } from "../Screen/ScreenSection";
import ResizableComponent from "../../Layouts/ResizableComponent";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import CreateRdbmsFormModal from "./RDBMSForm/CreateRdbmsFormModal";
import { toastError } from "../../utils/toastUtils";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";

const Form = () => {
  const [isLoader, setIsLoader] = useState(true);
  const [isError, setIsError] = useState(false);

  const [newBlockData, setNewBlockData] = useState(null);
  const [newRdbmsBlockData, setNewRdbmsBlockData] = useState(null);
  const { getAllTableData, getAllAppuserData } = useTableContext();
  const { selectedForm, fetchAllForms, setSelectedForm } = useFormContext();
  const { formsExplorerData, setFormsExplorerData } = useTreeContext();
  const { axiosInstance, handleError, handleSuccess } = useAxiosContext();
  const { getUserConfirmationForDelete } = useModalContext();
  const { orgId, appId } = useParams();

  const navigate = useNavigate();

  const createTemplate = async (folder, config) => {
    try {
      const { data, message } = await axiosInstance.post(`/${folder.parentData}`, config);
      let newItem = fileFolderConfig.getFileConfig(data.id, data.name, folder.parentData, true, true, "", folder.type);
      if (folder.parentData === "subform") {
        newItem = fileFolderConfig.getFileConfig(
          `subform/${data.id}`,
          data.name,
          `${data.config.table_id || ""}/statemachine`,
          true,
          true,
          "",
          "subform"
        );
      }
      if (folder.parentData === "rdbmsform") {
        newItem = fileFolderConfig.getFileConfig(data.id, data.name, "rdbmsform", true, true, "", "rdbmsform");
      }
      const updatedTree = insertNewFileOrFolder(formsExplorerData, folder, newItem);
      setFormsExplorerData(updatedTree);
      if (folder.parentData === "subform") {
        navigate(`subform/${data.id}`);
      } else {
        navigate(`./${data.id}`);
      }
      handleSuccess(message);
    } catch (error) {
      handleError(error);
    }
  };

  const handleNewInsert = async (folder, name) => {
    if (folder.insert_type === "popup" && folder.name === "Subform") {
      setNewBlockData(folder);
      return;
    }

    if (folder.insert_type === "popup" && folder.name === "Rdbms Forms") {
      setNewRdbmsBlockData(folder);
      return;
    }

    if (name.trim().length < 3) {
      toastError("Name must be at least 3 characters");
      return;
    }
    const config = { name };
    await createTemplate(folder, config);
  };

  const createSubform = async (config) => {
    await createTemplate(newBlockData, config);
    setNewBlockData(null);
  };

  const createRdbmsform = async (config) => {
    await createTemplate(newRdbmsBlockData, config);
    setNewRdbmsBlockData(null);
  };

  const handleEdit = async (folder, newName) => {
    if (newName.trim().length < 3) {
      toastError("Name must be at least 3 characters");
      return;
    }
    try {
      const path = folder.type === "subform" ? folder.id : `${folder.type}/${folder.id}`;
      const { message } = await axiosInstance.patch(`/${path}`, { name: newName });
      const newTree = editFolderOrFile(formsExplorerData, folder, newName);
      setFormsExplorerData(newTree);

      if (folder.id === selectedForm?.id) {
        setSelectedForm({ ...selectedForm, name: newName });
      }

      handleSuccess(message);
    } catch (error) {
      handleError(error);
    }
  };

  const handleDelete = async (folder) => {
    const userConfirmed = await getUserConfirmationForDelete(folder.name);
    if (!userConfirmed) return;
    try {
      const path = folder.type === "subform" ? folder.id : `${folder.type}/${folder.id}`;
      const { message } = await axiosInstance.delete(`/${path}`);
      const newTree = deleteFolderOfFile(formsExplorerData, folder);
      setFormsExplorerData(newTree);
      if (folder.id === selectedForm?.id) {
        navigate("./");
      }
      handleSuccess(message);
    } catch (error) {
      handleError(error);
    }
  };

  const callbackFunction = (id) => {
    const parts = id.split("/");

    let targetPath = "";

    if (parts.includes("statemachine")) {
      const smId = parts[0];
      const formId = parts[2];
      targetPath = `/organization/${orgId}/app/${appId}/smform/${smId}/form/${formId}`;
    } else if (parts[0] === "customfunction") {
      const functionId = parts[1];
      targetPath = `/organization/${orgId}/app/${appId}/smform/customfunction/${functionId}`;
    } else if (parts[0] === "subform") {
      const subformId = parts[1];
      targetPath = `/organization/${orgId}/app/${appId}/smform/subform/${subformId}`;
    }

    if (targetPath) {
      navigate(targetPath);
    }
  };
  const initialFetching = async () => {
    try {
      setIsLoader(true);
      setIsError(false);
      await getAllTableData();
      await getAllAppuserData();
      if (!formsExplorerData) {
        await fetchAllForms();
      }
    } catch (error) {
      handleError(error);
      setIsError(true);
    } finally {
      setIsLoader(false);
    }
  };
  useEffect(() => {
    initialFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <section className="main_section">
        <div className="sub_section">
          {isError ? (
            <TryAgainMessage retryFunction={initialFetching} />
          ) : (
            <>
              <ResizableComponent
                resizeSide="right"
                storageKey="left_sidebar_size"
                defaultSize={244}
                range={[244, 400]} // Specify the range as [minSize, maxSize]
                collapsible={true}
              >
                <HeaderModes />
                <div className="d_flex">
                  <Navbar />
                  <LeftSideBar
                    explorerData={formsExplorerData}
                    setExplorerData={setFormsExplorerData}
                    selectedScreen={selectedForm}
                    handleNewInsert={handleNewInsert}
                    handleDelete={handleDelete}
                    handleEdit={handleEdit}
                    callbackFunction={callbackFunction}
                    type="Forms"
                    isLoader={isLoader}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <FolderCollapse
                        explorerData={formsExplorerData}
                        setExplorerData={setFormsExplorerData}
                        type="form"
                      />
                    </div>
                  </LeftSideBar>
                </div>
              </ResizableComponent>
              {!isLoader ? <Outlet /> : <Loader />}
            </>
          )}
        </div>
      </section>
      <BuilderModal isOpen={newBlockData} onClose={() => setNewBlockData(null)} modalName={"Add Subform"}>
        <CreateSubformModal create={createSubform} />
      </BuilderModal>
      <BuilderModal isOpen={newRdbmsBlockData} onClose={() => setNewRdbmsBlockData(null)} modalName={"Add RDBMS Form"}>
        <CreateRdbmsFormModal create={createRdbmsform} />
      </BuilderModal>
    </>
  );
};

export default Form;
